import { NotificationButton } from "../../components/NotificationButton";
import { AchievementFullView } from "./components/AchievementFullView";
import { AchievementsWidget } from "./components/AchievementsWidget";
import { BadgeCenterActivity } from "./components/BadgeCenterActivity";
import { BadgeCenterFieldPro1 } from "./components/BadgeCenterFieldPro1";
import { BadgeCenterWeeklyGoals } from "./components/BadgeCenterWeeklyGoals";
import { BadgeCurrentStatus } from "./components/BadgeCurrentStatus";
import { useEffect, useState } from "react";
import { StartWithPraxisplayPopup } from "./components/StartWithPraxisplayPopup";
import { BadgeCenterOnboarding } from "./onboarding/BadgeCenterOnboarding";
import { useBadgeCenterOnboarding } from "./onboarding/useBadgeCenterOnboarding";
import { TierScreen } from "./components/TierScreen";
import { BadgeCenterSkeleton } from "./components/BadgeCenterSkeleton";
import { api as badgeCenterApi } from "../../services/apis/badge-center";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../../services/user-context";

export type BadgeCenterProps = {
    tierMilestoneList?: any[];
    tierLastGainedMilestone?: number;
};

export default function BadgeCenter() {
    const { userInfo } = useUser() as any;
    const [isAchievementFullView, setIsAchievementFullView] = useState(false);
    const [isTierScreen, setIsTierScreen] = useState(false);
    const [showStartWithPraxisplayPopup, setShowStartWithPraxisplayPopup] = useState(false);
    
    const { data: userState, isLoading: isUserStateLoading } = useQuery({
        queryKey: ["user-state"],
        queryFn: () => badgeCenterApi.getUserState(),
    });


    const { data: activeView, isLoading: isActiveViewLoading } = useQuery({
        queryKey: ["active-view"],
        queryFn: () => badgeCenterApi.getActiveView(),
        enabled: !!userInfo?.id,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const {
        data: tierProgress,
        isLoading: isTierProgressLoading,
        isError: isTierProgressError,
        isFetching: isTierProgressFetching,
        refetch: refetchTierProgress,
    } = useQuery({
        queryKey: ["tier-progress", userInfo?.id],
        queryFn: () => badgeCenterApi.getTierProgress(),
        enabled: !!userInfo?.id,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const { data: weeklyGoals, isLoading: isWeeklyGoalsLoading } = useQuery({
        queryKey: ["weekly-goals", userInfo?.id],
        queryFn: () => badgeCenterApi.getWeeklyGoals(),
        enabled: !!userInfo?.id,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const { data: weeklyGoalsWarning } = useQuery({
        queryKey: ["weekly-goals-warning", userInfo?.id],
        queryFn: () => badgeCenterApi.getWeeklyGoalsWarning(),
        enabled: !!userInfo?.id,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const { data: goalsLevel, isLoading: isGoalsLevelLoading } = useQuery({
        queryKey: ["goals-level", userInfo?.id],
        queryFn: () => badgeCenterApi.getGoalsLevel(),
        enabled: !!userInfo?.id,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const userStateData = userState?.data;
    const isBadgeCenterLocked = !userStateData?.hasCompletedSession;

    const isUserStateReady = !!userInfo?.id && !isUserStateLoading;

    // Show the onboarding carousel only once the Badge Center is actually
    // accessible, so it never overlaps the locked-state "Start with PraxisPlay" popup.
    const {
        shouldShow: showOnboarding,
        cards: onboardingCards,
        dismiss: dismissOnboarding,
        step: onboardingStep,
        setStep: setOnboardingStep,
    } = useBadgeCenterOnboarding(isUserStateReady && !isBadgeCenterLocked);

    const handleAchievementFullView = () => {
        setIsAchievementFullView(true);
    }

    const handleCloseAchievementFullView = () => {
        setIsAchievementFullView(false);
    }

    const handleExploreBadgeCenter = () => {
        setShowStartWithPraxisplayPopup(false);
    }

    const handleTierScreen = () => {
        setIsTierScreen(true);
    }

    const handleCloseTierScreen = () => {
        setIsTierScreen(false);
    }
    
    const isContentLoading =
        isActiveViewLoading ||
        isTierProgressLoading ||
        isWeeklyGoalsLoading ||
        isGoalsLevelLoading;

    useEffect(() => {
        if (!isUserStateReady) {
            return;
        }

        if (isBadgeCenterLocked) {
            setShowStartWithPraxisplayPopup(true);
        } else {
            setShowStartWithPraxisplayPopup(false);
        }
    }, [isBadgeCenterLocked, isUserStateReady])

    const showFullSkeleton = !isUserStateReady || isContentLoading;

    return (

        <div className="p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div className="flex flex-col">
                    <div className="text-black text-[34px] font-semibold">Badge Center</div>
                    <div className="justify-start text-[#999999] text-[15px] font-medium">Reach your goals and become a PraxisPro!</div>
                </div>
                <NotificationButton />
            </div>
            {isAchievementFullView ? <AchievementFullView onCloseAchievementFullView={handleCloseAchievementFullView} /> 
            : isTierScreen ? (
                <TierScreen
                    onCloseTierScreen={handleCloseTierScreen}
                    tierProgress={tierProgress}
                    isLoading={isTierProgressLoading}
                    isError={isTierProgressError}
                    isRefetching={isTierProgressFetching && !isTierProgressLoading}
                    onRetry={refetchTierProgress}
                />
            )
            : <div className="flex flex-col gap-4 relative">
                {showFullSkeleton ? (
                    <BadgeCenterSkeleton />
                ) : (
                    <>
                        <BadgeCurrentStatus xpProgress={activeView} isLocked={isBadgeCenterLocked} onViewTierProgression={handleTierScreen} />
                        <div className="flex flex-col xl:flex-row gap-4 items-stretch">
                            <div className="flex flex-[5] flex-col gap-4">
                                <BadgeCenterActivity isLocked={isBadgeCenterLocked} totalXPPoints={activeView?.total_xp || 0} />
                                <AchievementsWidget onAchievementFullView={handleAchievementFullView} isLocked={isBadgeCenterLocked} />
                            </div>
                            <div className="flex flex-[4] flex-col gap-4">
                                <BadgeCenterWeeklyGoals isLocked={isBadgeCenterLocked} weeklyGoals={weeklyGoals} weeklyGoalsWarning={weeklyGoalsWarning} />
                                <BadgeCenterFieldPro1 isLocked={isBadgeCenterLocked} goalsLevel={goalsLevel} />
                            </div>
                        </div>
                    </>
                )}
                {showStartWithPraxisplayPopup && <StartWithPraxisplayPopup onExploreBadgeCenter={handleExploreBadgeCenter} />}
            </div>}
            {showOnboarding && !isAchievementFullView && !isTierScreen && (
                <BadgeCenterOnboarding
                    cards={onboardingCards}
                    onDismiss={dismissOnboarding}
                    step={onboardingStep}
                    setStep={setOnboardingStep}
                />
            )}
        </div>
    )
}